import React, {useState, useEffect} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	FormControlLabel,
	Switch,
	Typography,
	Box,
	Grid,
	Paper
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import API from "../../../../network/dashboardAPI";
import UploadService from "../../../../services/FileUploadService";
import useAlert from "../../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
	contentPaper: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc"
	},
	field: {
		marginBottom: theme.spacing(2)
	},
	uploadButton: {
		marginTop: theme.spacing(1)
	},
	imagePreview: {
		width: 380,
		height: 380,
		borderRadius: 12,
		objectFit: "cover",
		marginTop: theme.spacing(1),
		border: "1px solid #ddd"
	},
	actions: {
		padding: theme.spacing(2)
	},
	saveButton: {
		minWidth: 120
	}
}));

export default function DelipointSettingDialog({open, onClose, onSaved, initialData}) {
	const classes = useStyles();
	const alert = useAlert();

	const [id, setId] = useState("");
	const [settingKey, setSettingKey] = useState("");
	const [settingValue, setSettingValue] = useState("");
	const [description, setDescription] = useState("");
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (initialData) {
			setId(initialData._id || "");
			setSettingKey(initialData.setting_key || "");
			setSettingValue(initialData.setting_value || "");
			setDescription(initialData.description || "");
		} else {
			setId("");
			setSettingKey("");
			setSettingValue("");
			setDescription("");
		}
		setErrors({});
	}, [initialData, open]);

	const validate = () => {
		const newErrors = {};
		if (settingValue === "") newErrors.setting_value = "Value is required";
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;

		try {
			setLoading(true);

			const payload = {
				id: id,
				setting_key: settingKey,
				setting_value: settingValue,
				description: description || null
			};

			let response;
			const isEdit = !!initialData?._id;

			if (isEdit) {
				response = await API.updateDelipointSetting(payload);
			} else {
				// Not supporting add for settings
				setLoading(false);
				return;
			}

			const res = await response.json();

			alert.close();

			if (res.success) {
				const action = "edit";
				const itemName = initialData?.setting_key;

				alert.success(
					`Updated Successfully!`,
					`"${itemName}" has been updated successfully.`
				);
				alert.toast(`Delipoint setting updated`, 'success');
				onSaved(action, itemName);
				onClose();
			} else {
				onClose();
				const errorMessage = res.message || `Failed to update delipoint setting`;
				console.error(errorMessage);
				alert.error(
					`Update Failed`,
					errorMessage
				);
			}
		} catch (err) {
			onClose();
			console.error("Error saving delipoint setting:", err);
			alert.close();
			alert.error(
				"Network Error",
				`Unable to update delipoint setting. Please check your connection and try again.`
			);
		} finally {
			setLoading(false);
		}
	};


	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				Edit Delipoint Setting
			</DialogTitle>
			<DialogContent>
				<Box p={2}>
					<TextField
						label="Key"
						fullWidth
						variant="outlined"
						className={classes.field}
						value={settingKey}
						InputProps={{ readOnly: true, style: { backgroundColor: '#f0f0f0' } }}
						margin="normal"
					/>
					<TextField
						label="Value"
						fullWidth
						variant="outlined"
						className={classes.field}
						value={settingValue}
						onChange={e => setSettingValue(e.target.value)}
						error={!!errors.setting_value}
						helperText={errors.setting_value}
						margin="normal"
					/>
					<TextField
						label="Description"
						fullWidth
						variant="outlined"
						className={classes.field}
						value={description}
						onChange={e => setDescription(e.target.value)}
						margin="normal"
						multiline
						rows={5}
					/>
				</Box>
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button onClick={onClose} color="default" variant="outlined" disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					color="primary"
					variant="contained"
					className={classes.saveButton}
					disabled={loading}
				>
					{loading ? "Saving..." : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
